import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { StateCard } from "@/ui/states";
import type { StepState } from "@/ui/route";
import { STATE_MARK } from "@/ui/route";

/* Мелкие общие элементы карты и трека. */

export function Eyebrow({ children, right, className = "" }: { children: ReactNode; right?: ReactNode; className?: string }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 font-mono text-[10.5px] uppercase tracking-[.06em] text-mut ${className}`}>
      <span>{children}</span>
      {right !== undefined && <span className="normal-case tracking-normal text-ink2">{right}</span>}
    </div>
  );
}

type LinkVariant = "primary" | "secondary" | "accent";

// Ссылка в виде кнопки (A0): цвет задан явно, как у <Button>.
const linkLook: Record<LinkVariant, string> = {
  primary: "border-acc bg-acc text-accink font-medium hover:brightness-[1.08]",
  secondary: "border-bd2 bg-transparent text-ink hover:border-acc hover:text-acc",
  accent: "border-acc bg-transparent text-acc hover:bg-accsoft",
};

export function StepLink({ id, variant = "secondary", full = false, children }: { id: string; variant?: LinkVariant; full?: boolean; children: ReactNode }) {
  return (
    <Link
      to="/step/$id"
      params={{ id }}
      className={`inline-flex items-center justify-center gap-2 rounded-sm border px-4 py-[9px] font-mono text-[13px] leading-none no-underline transition-colors ${linkLook[variant]} ${full ? "w-full" : ""}`}
    >
      {children}
    </Link>
  );
}

/** Кнопка шага по состоянию: пройден — открыть, в работе — продолжить, доступен — начать. */
export function StepActionLink({ id, state, full = false }: { id: string; state: Exclude<StepState, "locked">; full?: boolean }) {
  const variant: LinkVariant = state === "active" ? "primary" : state === "open" ? "accent" : "secondary";
  const label = state === "active" ? "продолжить шаг" : state === "open" ? "начать шаг" : "открыть шаг";
  return (
    <StepLink id={id} variant={variant} full={full}>
      {label}
    </StepLink>
  );
}

const chipLook: Record<StepState, string> = {
  done: "bg-oksoft text-ok border-accdim",
  active: "bg-accsoft text-acc border-acc",
  open: "bg-card2 text-ink2 border-bd2",
  locked: "bg-transparent text-mut border-bd border-dashed",
};

/** Чип состояния шага: марка + подпись, не только цвет. */
export function StateChip({ state }: { state: StepState }) {
  const { mark, label } = STATE_MARK[state];
  return (
    <span data-state={state} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-[3px] font-mono text-[11px] ${chipLook[state]}`}>
      <span aria-hidden>{mark}</span>
      {label}
    </span>
  );
}

const gateLook: Record<StepState, { label: string; cls: string }> = {
  done: { label: "сдан", cls: "bg-oksoft text-ok border-ok" },
  active: { label: "не сдан", cls: "bg-warnsoft text-warn border-warn" },
  open: { label: "доступен", cls: "bg-card2 text-ink2 border-bd2" },
  locked: { label: "закрыт", cls: "bg-transparent text-mut border-bd border-dashed" },
};

/** Чип гейта в строке шага (S3). */
export function GateChip({ state }: { state: StepState }) {
  const g = gateLook[state];
  return (
    <span data-gate={state} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-[3px] font-mono text-[11px] ${g.cls}`}>
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {g.label}
    </span>
  );
}

export function NetworkError({ retry }: { retry: () => void }) {
  return (
    <StateCard
      code="ERR_NETWORK"
      where="загрузка"
      tone="warn"
      title="Нет связи с сервером"
      body="Страница не смогла получить данные. Ничего не потеряно — обновите страницу, когда связь вернётся."
      primary={{ label: "повторить сейчас", onClick: retry }}
    />
  );
}
